import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ForecastCol } from "./ForecastCol";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { Tag } from "./Tag";
import { formatCoords } from "../lib/customLocation";
import { dayName, dayNum } from "../lib/utils";
import type { Coords, EnrichedSpot, SpeedUnit } from "../lib/types";
import styles from "./MapSpotCard.module.css";

interface MapSpotCardProps {
  spot: EnrichedSpot;
  unit: SpeedUnit;
  /** Dismiss the card — clears the spot selection. */
  onClose: () => void;
  /** When true, the card represents a dropped-pin custom location: the
   *  header shows coordinates instead of area/spot name, the "Best in"
   *  tags are hidden (no curation), and the click-through to a detail
   *  page is disabled (no detail page exists for a custom point). */
  customLocation?: boolean;
  /** Day strip's selected day — drives the per-column selected/dimmed
   *  treatment in the forecast row, mirroring the SpotCard behaviour. */
  selectedDay?: number;
}

/**
 * Floating spot card overlaid on the spots map — Figma node 207:5757
 * (default) and 209:11009 (hover). The close button + prev/next
 * chevrons sit OUTSIDE the dark container so the user can dismiss the
 * card or scroll the forecast strip without triggering the card's
 * click-through to the spot detail page.
 *
 * The chevrons scroll the day strip + forecast row horizontally — at
 * narrow widths not all 7 day cells fit, and the user pages through
 * them one viewport at a time. Both rows share a single scroll
 * container so they stay aligned.
 */
export function MapSpotCard({
  spot,
  unit,
  onClose,
  customLocation = false,
  selectedDay,
}: MapSpotCardProps) {
  const [location, setLocation] = useLocation();
  // Custom-location detail lives at /point/:lat/:lon and threads the
  // current overview path through `?back` so the navbar back button
  // returns the user to the same region overview they long-pressed on.
  // Carry the strip's selected day into the detail URL so tapping the
  // card lands on the same day the user was reviewing on the overview.
  const daySuffix = selectedDay !== undefined ? `?day=${selectedDay}` : "";
  const detailHref = customLocation
    ? `/point/${spot.coords.lat}/${spot.coords.lon}?back=${encodeURIComponent(location)}${
        selectedDay !== undefined ? `&day=${selectedDay}` : ""
      }`
    : `/spot/${spot.id}${daySuffix}`;
  const openDetail = () => setLocation(detailHref);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // Track whether the forecast strip can scroll left/right so the chevrons
  // disable at the bounds.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setCanPrev(el.scrollLeft > 1);
      setCanNext(el.scrollLeft < max - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [spot.days.length]);

  const scrollByPage = useCallback((dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  }, []);

  return (
    <div className={styles.mapSpotCard}>
      <div className={styles.controlHeader}>
        <IconButton
          variant="outline"
          onClick={onClose}
          aria-label="Close spot card"
        >
          <Icon name="close" />
        </IconButton>
        {(canPrev || canNext) && (
          <div className={styles.navButtons}>
            <IconButton
              variant="outline"
              disabled={!canPrev}
              onClick={() => scrollByPage(-1)}
              aria-label="Scroll forecast backward"
            >
              <Icon name="chevron-left" />
            </IconButton>
            <IconButton
              variant="outline"
              disabled={!canNext}
              onClick={() => scrollByPage(1)}
              aria-label="Scroll forecast forward"
            >
              <Icon name="chevron-right" />
            </IconButton>
          </div>
        )}
      </div>
      <div
        className={styles.container}
        onClick={openDetail}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openDetail();
          }
        }}
      >
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <span className={styles.cardArea}>{spot.area}</span>
            <span className={styles.cardName}>{spot.name}</span>
          </div>
          {!customLocation && (
            <div className={styles.cardHeaderRight}>
              <span className={styles.worksBestLabel}>Best in</span>
              <div className={styles.worksBestTags}>
                {spot.ideal.dirs.map((d) => (
                  <Tag key={d}>{d}</Tag>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={styles.forecastWrap}>
          <div
            ref={scrollRef}
            className={styles.forecastScroll}
            /* Drags on the strip scroll the strip — not the card click-
               through and not the page beneath. Stop the click on the
               strip from bubbling up so a release after a drag-scroll
               doesn't accidentally open the detail page. */
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.smallDaystrip}>
              {spot.days.map((day) => (
                <span key={day.dayOffset} className={styles.dayLabel}>
                  {dayName(day.date)} {dayNum(day.date)}
                </span>
              ))}
            </div>
            <div className={styles.forecastRow}>
              {spot.days.map((day) => (
                <ForecastCol
                  key={day.dayOffset}
                  day={day}
                  spotId={spot.id}
                  unit={unit}
                  idealDirs={spot.ideal.dirs}
                  {...(selectedDay !== undefined && { selectedDay })}
                  {...(customLocation && {
                    hrefFor: (dayOffset: number) =>
                      `/point/${spot.coords.lat}/${spot.coords.lon}?day=${dayOffset}&back=${encodeURIComponent(location)}`,
                  })}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Loading-state MapSpotCard for the long-press custom-location flow.
 * The coords are known the instant the user drops a pin, so we render
 * the card frame, close button and header (area + coords) immediately
 * and shimmer the forecast row while `/api/point` resolves. Mirrors
 * the partial-skeleton approach used by SkeletonSpotCard on the list.
 */
export function SkeletonMapSpotCard({
  coords,
  skeletonCount,
  onClose,
}: {
  coords: Coords;
  skeletonCount: number;
  onClose: () => void;
}) {
  return (
    <div className={styles.mapSpotCard}>
      <div className={styles.controlHeader}>
        <IconButton
          variant="outline"
          onClick={onClose}
          aria-label="Close spot card"
        >
          <Icon name="close" />
        </IconButton>
      </div>
      <div className={[styles.container, styles.containerSkeleton].join(" ")}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <span className={styles.cardArea}>Custom location</span>
            <span className={styles.cardName}>{formatCoords(coords)}</span>
          </div>
        </div>
        <div className={styles.forecastWrap}>
          <div className={styles.forecastScroll}>
            <div className={styles.smallDaystrip}>
              {Array.from({ length: skeletonCount }, (_, i) => (
                <span key={i} className={styles.dayLabelSkel} />
              ))}
            </div>
            <div className={styles.forecastRow}>
              {Array.from({ length: skeletonCount }, (_, i) => (
                <div key={i} className={styles.skeletonCol} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
